/**
 * Shared types for the durable agent mailbox system (Phase 1: Orchestrator).
 *
 * Message classes define escalating urgency levels for agent-to-agent
 * and orchestrator-to-agent communication.
 */
// ─── Message Classes (escalating urgency) ──────────────────────────────────
export const MESSAGE_CLASSES = [
    'notify',
    'deliver',
    'interrupt',
    'preempt_and_replan',
    'shutdown_with_final_prompt',
];
/** Classes that require durable storage and guaranteed delivery. */
export const DURABLE_MESSAGE_CLASSES = [
    'deliver',
    'interrupt',
    'preempt_and_replan',
    'shutdown_with_final_prompt',
];
/**
 * Numeric urgency rank per message class. Mirrors the SQL CASE ordering used by
 * the delivery claim engine (`prompt-delivery.ts` / `mailbox.ts`) so TypeScript
 * callers and SQL ordering can never drift apart.
 */
export const MESSAGE_CLASS_URGENCY = {
    notify: 1,
    deliver: 2,
    interrupt: 3,
    preempt_and_replan: 4,
    shutdown_with_final_prompt: 5,
};
/**
 * The lowest urgency rank that authorizes stop-and-deliver: a message of this
 * class or above may cancel the target's in-flight turn so it is delivered as
 * the very next prompt instead of parking in `retry_wait` behind the busy turn.
 */
export const TURN_STOP_URGENCY_THRESHOLD = MESSAGE_CLASS_URGENCY.interrupt;
/** True when a message class is urgent enough to trigger stop-and-deliver. */
export function isUrgentMessageClass(messageClass) {
    return MESSAGE_CLASS_URGENCY[messageClass] >= TURN_STOP_URGENCY_THRESHOLD;
}
// ─── Delivery State Machine ────────────────────────────────────────────────
export const DELIVERY_STATES = [
    'queued',
    'delivering',
    'retry_wait',
    'delivered',
    'acked',
    'failed',
    'ambiguous',
    'expired',
];
/** Valid state transitions for the delivery state machine. */
export const DELIVERY_STATE_TRANSITIONS = {
    queued: ['delivering', 'delivered', 'failed', 'ambiguous', 'expired'],
    delivering: ['retry_wait', 'delivered', 'failed', 'ambiguous', 'expired'],
    retry_wait: ['delivering', 'failed', 'ambiguous', 'expired'],
    delivered: ['acked', 'expired', 'queued'],
    acked: [],
    failed: [],
    ambiguous: [],
    expired: [],
};
export const DELIVERY_TERMINAL_STATES = [
    'acked',
    'failed',
    'ambiguous',
    'expired',
];
export const PROMPT_DELIVERY_SOURCES = [
    'user_followup',
    'comment_directive',
    'agent_mailbox',
    'orchestration_handoff',
    // SAM's first prompt to a restored session that has nothing queued (`restored-session-prompt.ts`).
    'checkpoint_continuation',
    'parent_wakeup',
    'project_event_wake',
    'github_trigger',
    'scheduled_action',
    // A superadmin's approve/deny decision on an Operations action, delivered to the requesting
    // agent's session (`services/operations-actions/decision-delivery.ts`).
    'operations_decision',
];
export const VM_PROMPT_RECEIPT_STATES = [
    'accepted',
    'in_flight',
    'completed',
    'not_found',
    'ambiguous',
];
// ─── Sender identity ──────────────────────────────────────────────────────
export const SENDER_TYPES = ['agent', 'orchestrator', 'system', 'human'];
// ─── Configurable defaults ──────────────────────────────────���──────────────
export const MAILBOX_DEFAULTS = {
    /** Time to wait for ack before re-delivery (ms). Override: MAILBOX_ACK_TIMEOUT_MS */
    ACK_TIMEOUT_MS: 300_000, // 5 minutes
    /** Max delivery attempts before marking expired. Override: MAILBOX_REDELIVERY_MAX_ATTEMPTS */
    REDELIVERY_MAX_ATTEMPTS: 5,
    /** Default TTL for messages (ms). Override: MAILBOX_TTL_MS */
    TTL_MS: 3_600_000, // 1 hour
    /** Interval for DO alarm delivery sweep (ms). Override: MAILBOX_DELIVERY_POLL_INTERVAL_MS */
    DELIVERY_POLL_INTERVAL_MS: 30_000, // 30 seconds
    /** Max messages per project. Override: MAILBOX_MAX_MESSAGES_PER_PROJECT */
    MAX_MESSAGES_PER_PROJECT: 1_000,
    /** Max content length for messages. Override: MAILBOX_MESSAGE_MAX_LENGTH */
    MESSAGE_MAX_LENGTH: 32_768,
};
//# sourceMappingURL=mailbox.js.map